/**
 * 6 чіпів «Середньозважені ціни РДН» (2×3) для дашборду портфелю — праворуч від
 * мапи. Живі дані з GET /market/dam-summary. Весь блок займає одну картку (h-full).
 * Зміна: ↑ зелений / ↓ бурштин / → сірий (догма, без червоного).
 */
import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';

interface Zone { wavg: number | null; prevWavg: number | null; changePct: number | null }
interface Tiles {
  day: Zone; day_8_23: Zone; night: Zone; peak: Zone; semipeak: Zone;
  month: { ym: string; wavg: number | null; prevYm: string; prevWavg: number | null; changePct: number | null };
}

const num = (n: number | null | undefined, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

function Chip({ label, wavg, changePct, sub }:
  { label: string; wavg: number | null; changePct: number | null; sub: string }) {
  const up = changePct != null && changePct > 0, down = changePct != null && changePct < 0;
  const col = up ? 'var(--ok)' : down ? 'var(--warn)' : 'var(--text-dim)';
  return (
    <div className="tile px-3 py-2.5 flex flex-col justify-center gap-2.5 h-full min-w-0">
      {/* Верх: підпис ліворуч, зміна праворуч (по краях ширини) */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>{label}</span>
        {changePct != null && (
          <span className="text-[10.5px] font-semibold whitespace-nowrap shrink-0" style={{ color: col }}>
            {up ? '↑' : down ? '↓' : '→'} {num(Math.abs(changePct))}%
          </span>
        )}
      </div>
      {/* Низ: значення + одиниці/період в один рядок */}
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span className="font-display text-[17px] font-normal leading-none whitespace-nowrap"
          style={{ color: wavg == null ? 'var(--text-dim)' : 'var(--text)' }}>{num(wavg)}</span>
        <span className="text-[10px] truncate" style={{ color: 'var(--text-dim)' }}>{sub}</span>
      </div>
    </div>
  );
}

export default function RdnPriceChips({ className = '' }: { className?: string }) {
  const [t, setT] = useState<Tiles | null>(null);
  useEffect(() => {
    authFetch(`${API_BASE_URL}/market/dam-summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setT(d?.tiles ?? null))
      .catch(() => setT(null));
  }, []);
  const D1 = 'грн/МВт·год · vs D−1';
  return (
    <div className={`grid grid-cols-2 grid-rows-3 gap-2.5 h-full ${className}`}>
      <Chip label="За добу" wavg={t?.day.wavg ?? null} changePct={t?.day.changePct ?? null} sub={D1} />
      <Chip label="День (8-23)" wavg={t?.day_8_23.wavg ?? null} changePct={t?.day_8_23.changePct ?? null} sub={D1} />
      <Chip label="Ніч (1-7, 24)" wavg={t?.night.wavg ?? null} changePct={t?.night.changePct ?? null} sub={D1} />
      <Chip label="Пік (18-23)" wavg={t?.peak.wavg ?? null} changePct={t?.peak.changePct ?? null} sub={D1} />
      <Chip label="Напівпік (8-17)" wavg={t?.semipeak.wavg ?? null} changePct={t?.semipeak.changePct ?? null} sub={D1} />
      <Chip label="За місяць" wavg={t?.month.wavg ?? null} changePct={t?.month.changePct ?? null}
        sub={t ? `грн/МВт·год · ${t.month.ym} vs ${t.month.prevYm}` : 'грн/МВт·год'} />
    </div>
  );
}
